import React from 'react';
import { SidePanelContentCard } from '@brighterion/br-ui';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { IMPORT_EXPORT_PROCESS_TYPE } from 'Common/enums';
import { useListFilesDownloadStore } from 'Hooks/zustand/useListFilesDownloadStore';
import useGetSidePanelContentCardHelperData from '../../hooks/useGetSidePanelContentCardHelperData';
import useHandleImportExportProcessCancel from '../../hooks/useHandleImportExportProcessCancel';
import SidePanelContentCardHelper from './SidePanelContentCardHelper';

type SidePanelAccordionItemContentCardProps = {
    id: number;
    itemData: SidePanelAccordionItemContentCardData;
    expandedCardIndex: number;
    handleCardHeaderOnClick: (cardIndex: number, isExpanded: boolean) => void;
};

const SidePanelAccordionItemContentCard = ({
    id,
    itemData,
    expandedCardIndex,
    handleCardHeaderOnClick
}: SidePanelAccordionItemContentCardProps) => {
    const listFilesDownloadStore = useListFilesDownloadStore();
    const { confirmCancelModal } = useHandleImportExportProcessCancel();
    const { getHelperData, isHelperDataAvailable } = useGetSidePanelContentCardHelperData(itemData);
    const cardHelperData = React.useMemo(() => {
        return getHelperData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemData]);

    const cancellationProps: CancelImportExportProcess = {
        id: Number(itemData.cancelActionDetails?.id),
        status: itemData.status.type,
        type: itemData.entryActionType,
        listName: itemData.entryName
    };

    const onActionCallback = () => {
        if (itemData.downloadActionDetails) {
            const { isDownloadAllowed, downloadFileName, isCancelDownload } = itemData.downloadActionDetails;
            /* since batch job controls which file is available for download, 
        currently zip means export List Entries & if fileName has -error- as substring, means its an error file.
        */
            const isErrorFile = !downloadFileName?.endsWith('.zip') && downloadFileName?.includes('-error-');
            if (isDownloadAllowed && downloadFileName && !isCancelDownload) {
                const trackEventData = isErrorFile
                    ? 'Queue - Download Error Files'
                    : 'Queue - Download List Entry Files';
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: trackEventData,
                        text: trackEventData
                    }
                });
                listFilesDownloadStore.addFileToDownloading({
                    currentFileName: itemData?.downloadActionDetails.downloadFileName,
                    processType: itemData?.entryActionType as IMPORT_EXPORT_PROCESS_TYPE,
                    id: itemData.id,
                    fileParts: []
                });
            } else if (isCancelDownload) {
                const trackEventData = isErrorFile
                    ? 'Queue - Cancel Download Error Files'
                    : 'Queue - Cancel Download List Entry Files';
                trackEvent({
                    eventType: EventTypes.track,
                    eventName: EventNames.click,
                    payload: {
                        clickIdentifier: trackEventData,
                        text: trackEventData
                    }
                });
                listFilesDownloadStore.removeFileFromDownloading(itemData.id);
            }
        } else {
            confirmCancelModal(cancellationProps);
        }
    };
    const actionDetails = {
        onAction: onActionCallback,
        isActionButtonDisabled: false
    };

    return (
        <SidePanelContentCard
            id={id}
            itemData={itemData}
            expandedCardIndex={expandedCardIndex}
            handleCardHeaderOnClick={handleCardHeaderOnClick}
            actionDetails={actionDetails}
        >
            {isHelperDataAvailable && cardHelperData ? (
                <SidePanelContentCardHelper cardHelperData={cardHelperData} />
            ) : undefined}
        </SidePanelContentCard>
    );
};

export default SidePanelAccordionItemContentCard;
